import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { buildAffordabilityReading } from './helpers'
import {
  MeterRoot,
  TrackRow,
  Track,
  Fill,
  PercentLabel,
  BandLabel,
  NoIncomeNote,
} from './styled'
import { AffordabilityMeterProps } from './types'

const MAX_VISIBLE_PERCENT = 100

/**
 * Compact track + percent meter showing how a listing's monthly rent
 * lines up against the renter's stored take-home income. Falls back to
 * a soft "no income set" hint when the renter hasn't entered an income
 * yet — never shouts at them.
 *
 * @param {AffordabilityMeterProps} props
 * @returns {JSX.Element | null}
 */
const AffordabilityMeter = ({
  monthlyRent,
  size = 'md',
  withLabel = false,
}: AffordabilityMeterProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()

  if (profile === null) {
    return null
  }

  const monthlyTakeHome = profile.monthlyTakeHomeIncome

  if (monthlyTakeHome === null || monthlyTakeHome <= 0) {
    if (withLabel === false) {
      return null
    }
    return (
      <MeterRoot size={size}>
        <NoIncomeNote>Add take-home in profile to see fit.</NoIncomeNote>
      </MeterRoot>
    )
  }

  const reading = buildAffordabilityReading(monthlyRent, monthlyTakeHome)

  if (reading.hasIncome === false) {
    return null
  }

  const fillWidth = Math.min(reading.percent, MAX_VISIBLE_PERCENT)

  return (
    <MeterRoot size={size}>
      <TrackRow>
        <Track size={size}>
          <Fill band={reading.band} style={{ width: `${fillWidth}%` }} />
        </Track>
        <PercentLabel size={size} band={reading.band}>
          {reading.percent}%
        </PercentLabel>
      </TrackRow>
      {withLabel === true ? <BandLabel>{reading.label}</BandLabel> : null}
    </MeterRoot>
  )
}

export default AffordabilityMeter
